import type { ReactElement } from 'react'
import { APP_ROUTE_PERMISSIONS, canOpenAppPath, hasPermission, isRole } from '@k1p4/shared'
import type { Permission } from '@k1p4/shared'
import type { IconProps } from '@k1p4/ui'
import {
  Bank,
  Calendar,
  ChartBar,
  Clock,
  Document,
  Grid,
  GraduationCap,
  Home,
  Inbox,
  Plug,
  Settings,
  Shield,
  ShieldPlus,
  Users,
  Wallet,
  Workflow,
} from '@k1p4/ui'

export interface NavItem {
  /** Klucz i18n w gałęzi `nav` / `portalNav`. */
  key: string
  path: string
  icon: (props: IconProps) => ReactElement
  /**
   * Uprawnienia otwierające pozycję (§5.3). Brak listy = pozycja widoczna dla
   * każdej roli mającej dostęp do `/app`. Nawigacja jedynie odzwierciedla
   * uprawnienia — właściwą kontrolą jest guard po stronie API.
   */
  permissions?: readonly Permission[]
  /** Pozycja bez odpowiednika w makiecie (rejestr odstępstw, plan §18 D11). */
  offSpec?: boolean
}

/** Pozycje widoczne dla danego zestawu ról (§5.3: „nawigacja filtrowana per rola"). */
export function navForRoles(
  items: readonly NavItem[],
  /** Role z sesji przychodzą jako zwykłe stringi — filtrujemy je do znanych. */
  roles: readonly string[],
): readonly NavItem[] {
  const known = roles.filter(isRole)
  return items.filter(
    (item) =>
      item.permissions === undefined ||
      item.permissions.some((permission) => hasPermission(known, permission)),
  )
}

/*
 * Mapa tras modułów i dopasowanie ścieżki mieszkają w @k1p4/shared — z tej samej
 * mapy API filtruje podpowiedzi asystenta (Fala 4). Tu tylko je udostępniamy.
 */
export { APP_ROUTE_PERMISSIONS, canOpenAppPath }

/**
 * Pierwszy moduł, który dana rola może otworzyć — wejście `/app` prowadzi
 * właśnie tam. `null` znaczy, że rola nie ma w aplikacji HR nic do roboty
 * (konto wyłącznie portalowe) i należy jej się ekran odmowy z drogą do portalu.
 */
export function firstAllowedAppPath(roles: readonly string[]): string | null {
  return navForRoles(APP_NAV, roles)[0]?.path ?? null
}

/*
 * Nawigacja aplikacji HR — 14 pozycji ze spec 01 §0.1 w oryginalnej kolejności,
 * plus „Wnioski" (widok HR wniosków portalowych) dodane po „Pracownicy" [D11].
 * Bez badge'y z makiety („7", „3") — to były dane przykładowe projektanta [D19].
 */
export const APP_NAV: readonly NavItem[] = [
  {
    key: 'dashboard',
    path: '/app/dashboard',
    icon: Grid,
    permissions: APP_ROUTE_PERMISSIONS['/app/dashboard'],
  },
  {
    key: 'employees',
    path: '/app/employees',
    icon: Users,
    permissions: APP_ROUTE_PERMISSIONS['/app/employees'],
  },
  {
    key: 'requests',
    path: '/app/requests',
    icon: Inbox,
    permissions: APP_ROUTE_PERMISSIONS['/app/requests'],
    offSpec: true,
  },
  {
    key: 'documents',
    path: '/app/documents',
    icon: Document,
    permissions: APP_ROUTE_PERMISSIONS['/app/documents'],
  },
  {
    key: 'time',
    path: '/app/time',
    icon: Clock,
    permissions: APP_ROUTE_PERMISSIONS['/app/time'],
  },
  {
    key: 'leaves',
    path: '/app/leaves',
    icon: Calendar,
    permissions: APP_ROUTE_PERMISSIONS['/app/leaves'],
  },
  {
    key: 'health',
    path: '/app/health',
    icon: ShieldPlus,
    permissions: APP_ROUTE_PERMISSIONS['/app/health'],
  },
  {
    key: 'payroll',
    path: '/app/payroll',
    icon: Wallet,
    permissions: APP_ROUTE_PERMISSIONS['/app/payroll'],
  },
  {
    key: 'declarations',
    path: '/app/declarations',
    icon: Bank,
    // moduł deklaracji jest następstwem płac — widzi go każdy z podglądem płac
    permissions: APP_ROUTE_PERMISSIONS['/app/declarations'],
  },
  {
    key: 'workflows',
    path: '/app/workflows',
    icon: Workflow,
    permissions: APP_ROUTE_PERMISSIONS['/app/workflows'],
  },
  {
    key: 'development',
    path: '/app/development',
    icon: GraduationCap,
    permissions: APP_ROUTE_PERMISSIONS['/app/development'],
  },
  {
    key: 'reports',
    path: '/app/reports',
    icon: ChartBar,
    permissions: APP_ROUTE_PERMISSIONS['/app/reports'],
  },
  {
    key: 'integrations',
    path: '/app/integrations',
    icon: Plug,
    permissions: APP_ROUTE_PERMISSIONS['/app/integrations'],
  },
  {
    key: 'settings',
    path: '/app/settings',
    icon: Settings,
    permissions: APP_ROUTE_PERMISSIONS['/app/settings'],
  },
  {
    key: 'audit',
    path: '/app/audit',
    icon: Shield,
    permissions: APP_ROUTE_PERMISSIONS['/app/audit'],
  },
]

/** Portal pracownika — 9 zakładek ze spec 03. */
export const PORTAL_NAV: readonly NavItem[] = [
  { key: 'home', path: '/me', icon: Home },
  { key: 'documents', path: '/me/documents', icon: Document },
  { key: 'time', path: '/me/time', icon: Clock },
  { key: 'leaves', path: '/me/leaves', icon: Calendar },
  { key: 'health', path: '/me/health', icon: ShieldPlus },
  { key: 'pay', path: '/me/pay', icon: Wallet },
  { key: 'tax', path: '/me/tax', icon: Bank },
  { key: 'requests', path: '/me/requests', icon: Inbox },
  { key: 'growth', path: '/me/growth', icon: GraduationCap },
]
